/**
 * El plan que el visitante eligió ANTES de tener cuenta (D1 del PRD #297).
 *
 * xotski.com y la propia página de precios entregan al visitante a
 * `register?plan=xk1&events=…` con toda su configuración. Hasta aquí ese
 * `plan` se perdía por el camino: la persona se registraba, caía en el inicio
 * y tenía que volver a Precios a pagar. Ahora el plan se guarda al entrar en
 * el alta o en el acceso, sobrevive al OTP y a cualquier recarga, y al terminar
 * la autenticación se reanuda: `/pricing?…&resume=1`, que abre el pago de ese
 * plan directamente.
 *
 * Vive en `sessionStorage` a propósito: es de esta pestaña y de este intento,
 * no una preferencia. Caduca a las 24 h por si la pestaña se queda abierta.
 */
const KEY = 'xotski.pendingPlan';
const TTL_MS = 24 * 60 * 60 * 1000;

export type PendingTier = 'xk1' | 'xk2' | 'free' | 'alc';
const TIERS: PendingTier[] = ['xk1', 'xk2', 'free', 'alc'];

export interface PendingPlan {
  plan: PendingTier;
  /** La misma query del handoff (events, xotskies, brands…, lang), sin `email` ni `resume`. */
  query: string;
  at: number;
}

export function isPendingTier(v: string | null | undefined): v is PendingTier {
  return !!v && (TIERS as string[]).includes(v);
}

function write(p: PendingPlan | null) {
  try {
    if (p) sessionStorage.setItem(KEY, JSON.stringify(p));
    else sessionStorage.removeItem(KEY);
  } catch { /* almacenamiento bloqueado: el flujo sigue, sólo sin reanudar */ }
}

/** Guarda el plan de una query de handoff (`?plan=xk1&events=…`). Devuelve lo guardado, o null si no traía plan. */
export function capturePendingPlan(sp: URLSearchParams): PendingPlan | null {
  const plan = sp.get('plan');
  if (!isPendingTier(plan)) return null;
  const q = new URLSearchParams(sp);
  q.delete('email');
  q.delete('resume');
  const p: PendingPlan = { plan, query: q.toString(), at: Date.now() };
  write(p);
  return p;
}

/** Guarda un plan y su configuración ya serializada (desde la propia página de precios). */
export function savePendingPlan(plan: PendingTier, query: string): void {
  write({ plan, query, at: Date.now() });
}

/** Lo pendiente, si lo hay y no ha caducado. No lo consume. */
export function peekPendingPlan(): PendingPlan | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as PendingPlan;
    if (!isPendingTier(p?.plan) || typeof p.at !== 'number' || Date.now() - p.at > TTL_MS) {
      write(null);
      return null;
    }
    return p;
  } catch {
    return null;
  }
}

/** Lo pendiente, consumiéndolo: a partir de aquí ya no se reanuda otra vez. */
export function takePendingPlan(): PendingPlan | null {
  const p = peekPendingPlan();
  if (p) write(null);
  return p;
}

/**
 * A dónde ir tras autenticarse. Con un plan de pago pendiente, a Precios con
 * `resume=1`, que abre su pago sin más clics; Free y à la carte no tienen
 * nada que pagar al nacer la cuenta, así que al inicio (Free ya viene puesto).
 */
export function afterAuthPath(fallback = '/'): string {
  const p = takePendingPlan();
  if (!p) return fallback;
  if (p.plan === 'xk1' || p.plan === 'xk2') return `/pricing?${p.query}&resume=1`;
  if (p.plan === 'alc') return `/pricing?${p.query}&mode=calc`;
  return fallback;
}

/** Nombre de pantalla del plan pendiente. */
export function pendingPlanLabel(p: PendingPlan): string {
  return p.plan === 'xk1' ? 'XK1' : p.plan === 'xk2' ? 'XK2' : p.plan === 'free' ? 'Free' : 'À la carte';
}
